// WhatsApp Notification Service
// Supports Twilio WhatsApp API - configure in Settings

const WHATSAPP_CONFIG_KEY = 'hmgc_whatsapp_config';

export const getWhatsAppConfig = () => {
  try {
    const config = localStorage.getItem(WHATSAPP_CONFIG_KEY);
    return config ? JSON.parse(config) : null;
  } catch (e) {
    console.error('Error reading WhatsApp config:', e);
    return null;
  }
};

export const saveWhatsAppConfig = (config) => {
  try {
    localStorage.setItem(WHATSAPP_CONFIG_KEY, JSON.stringify(config));
    return true;
  } catch (e) {
    console.error('Error saving WhatsApp config:', e);
    return false;
  }
};

export const isWhatsAppConfigured = () => {
  const config = getWhatsAppConfig();
  return config && config.accountSid && config.authToken && config.fromNumber;
};

// Message templates for different notification types
export const MESSAGE_TEMPLATES = {
  LOAD_ASSIGNED: (load, driver) => 
    `🚛 New Load Assignment!\n\nHi ${driver.name},\n\nYou've been assigned Load #${load.load_number}.\n\nCustomer: ${load.customer?.company_name || 'N/A'}\nCommodity: ${load.commodity || 'N/A'}\nWeight: ${load.weight_kg ? load.weight_kg + ' kg' : 'N/A'}\n\nPlease confirm receipt of this assignment.`,
  
  STATUS_UPDATE: (load, newStatus) =>
    `📦 Load Status Update\n\nLoad #${load.load_number} status changed to: ${newStatus.replace(/_/g, ' ').toUpperCase()}\n\nCustomer: ${load.customer?.company_name || 'N/A'}`,
  
  PICKUP_REMINDER: (load, stop) =>
    `⏰ Pickup Reminder\n\nLoad #${load.load_number}\nPickup at: ${stop.address}\nTime Window: ${stop.time_window_start || 'ASAP'} - ${stop.time_window_end || 'Flexible'}\n\nPlease confirm when you arrive.`,
  
  DELIVERY_REMINDER: (load, stop) =>
    `📍 Delivery Reminder\n\nLoad #${load.load_number}\nDeliver to: ${stop.address}\nTime Window: ${stop.time_window_start || 'ASAP'} - ${stop.time_window_end || 'Flexible'}\n\nPlease confirm when delivered.`,
  
  DRIVER_CONFIRMATION_REQUEST: (load) =>
    `✅ Please Confirm\n\nLoad #${load.load_number}\n\nReply with:\n1 - Accept assignment\n2 - Decline assignment\n3 - Request more info`,

  DISPATCH_ALERT: (message) =>
    `🔔 Dispatch Alert\n\n${message}`,

  CUSTOM: (message) => message
};

// Send WhatsApp message via Twilio API
export const sendWhatsAppMessage = async (toNumber, messageType, data) => {
  const config = getWhatsAppConfig();
  
  if (!isWhatsAppConfigured()) {
    return {
      success: false,
      error: 'WhatsApp not configured. Please add Twilio credentials in Settings.',
      simulated: true
    };
  }

  // Format phone number for WhatsApp
  const formattedTo = toNumber.startsWith('whatsapp:') ? toNumber : `whatsapp:${toNumber}`;
  const formattedFrom = config.fromNumber.startsWith('whatsapp:') ? config.fromNumber : `whatsapp:${config.fromNumber}`;

  // Get message from template
  let messageBody;
  if (typeof messageType === 'function') {
    messageBody = messageType;
  } else if (MESSAGE_TEMPLATES[messageType]) {
    messageBody = MESSAGE_TEMPLATES[messageType](...(Array.isArray(data) ? data : [data]));
  } else {
    messageBody = data;
  }

  try {
    // Twilio API endpoint
    const url = `https://api.twilio.com/2010-04-01/Accounts/${config.accountSid}/Messages.json`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': 'Basic ' + btoa(`${config.accountSid}:${config.authToken}`),
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        To: formattedTo,
        From: formattedFrom,
        Body: messageBody
      })
    });

    const result = await response.json();

    if (response.ok) {
      return {
        success: true,
        messageSid: result.sid,
        status: result.status
      };
    } else {
      return {
        success: false,
        error: result.message || 'Failed to send message',
        code: result.code
      };
    }
  } catch (error) {
    console.error('WhatsApp send error:', error);
    return {
      success: false,
      error: error.message || 'Network error sending message'
    };
  }
};

// Simulate sending a message (for testing without API)
export const simulateWhatsAppMessage = (toNumber, messageType, data) => {
  let messageBody;
  if (typeof messageType === 'string' && MESSAGE_TEMPLATES[messageType]) {
    messageBody = MESSAGE_TEMPLATES[messageType](...(Array.isArray(data) ? data : [data]));
  } else {
    messageBody = data;
  }

  console.log('📱 Simulated WhatsApp Message:', {
    to: toNumber,
    message: messageBody
  });

  return {
    success: true,
    simulated: true,
    message: messageBody,
    to: toNumber
  };
};

// Send notification with fallback to simulation
export const sendNotification = async (toNumber, messageType, data) => {
  if (isWhatsAppConfigured()) {
    return await sendWhatsAppMessage(toNumber, messageType, data);
  } else {
    return simulateWhatsAppMessage(toNumber, messageType, data);
  }
};

// Batch send notifications to multiple recipients
export const sendBatchNotifications = async (recipients, messageType, data) => {
  const results = await Promise.all(
    recipients.map(async (recipient) => {
      const result = await sendNotification(recipient.phone, messageType, data);
      return {
        recipient: recipient.name || recipient.phone,
        ...result
      };
    })
  );

  return {
    total: results.length,
    successful: results.filter(r => r.success).length,
    failed: results.filter(r => !r.success).length,
    results
  };
};

export default {
  getWhatsAppConfig,
  saveWhatsAppConfig,
  isWhatsAppConfigured,
  sendWhatsAppMessage,
  simulateWhatsAppMessage,
  sendNotification,
  sendBatchNotifications,
  MESSAGE_TEMPLATES
};
